/*
 * The CustomerFinalizeStep component is the step in customer SO authorization flow where authorization takes place.
 * 
 * This takes the following props:
 *   auth                 - authentication object
 *   account              - customer for which this SO is being created
 *   project              - project for which this SO is being created
 *   assetArAuthBalance   - current balance amount for the project or account
 *   history              - for navigation after authorization
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React from 'react';
import PropTypes from 'prop-types';


import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Typography } from '@mui/material';
import AppBindStyles from '../../AppBindStyles';

import ArrowForward from '@mui/icons-material/ArrowForward';
import SavingSpinner from '../ui/SavingSpinner';


const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  }
});

class CustomerFinalizeStep extends React.Component {
  
  state = {
    showSpinner: false
  }

  showSpinner() {
    return this.state.showSpinner;
  }

  isInformationComplete() {   
    return true;
  }

  isBackDisabled() {
    if (this.showSpinner()) {
      return true;
    }

    if (this.props.activeStep === 0) {
      return true;
    }

    return false;
  }

  isNextDisabled() {
    return false;
  }

  authorizeAndPay() {
    this.setState({showSpinner: true});
    this.props.handleNext()
  }

  render() {
    let displayOnly = false;
    if (this.props.activeStep > this.props.assignedStep) {
      displayOnly = true;
    }

    let theAccount = this.props.account;
    let theProject = this.props.project;
    let theWallet  = null;
    if (theProject != null) {
      theWallet = theProject.walletTByCustomerWalletId;
    }

    let latestBudgetApproved = false;
    if (theAccount.useBudget === 0) {
      latestBudgetApproved = true;
    } else {
      if (!theProject) {
        if (theAccount.latestBudgetApproved === 1) {
          latestBudgetApproved = true;
        }
      } else {
        if (theProject.latestBudgetApproved === 1) {
          latestBudgetApproved = true;
        }
      }
    }
    
    let autoTopupsActive = false;
    if (theWallet != null) {
      if (theWallet.rAutoTopupActive !== null && theWallet.rAutoTopupActive === 1) {
        autoTopupsActive = true;
      } else if (theWallet.autoTopupActive === 1) {
        autoTopupsActive = true;
      }
    } else {
      if (theAccount.rAutoTopupActive != null && theAccount.rAutoTopupActive === 1) {
        autoTopupsActive = true;
      } else if (theAccount.autoTopupActive === 1) {
        autoTopupsActive = true;
      }
    }
    //console.log('rAutoTopupActive=' + theAccount.rAutoTopupActive);
    //console.log('autoTopupsActive=' + autoTopupsActive);

    let initialBalanceAmount = Number(this.props.account.initialBalanceAmount);
    let autoTopupAmount      = Number(this.props.account.rAutoTopupAmount);
    let autoTopupThreshold   = Number(this.props.account.rAutoTopupThreshold);
    let budgetApprovedCount  = Number(this.props.account.budgetApprovedCount);
    if (theWallet != null) {
      initialBalanceAmount = Number(theWallet.initialBalanceAmount);
      autoTopupAmount      = Number(theWallet.rAutoTopupAmount);
      autoTopupThreshold   = Number(theWallet.rAutoTopupThreshold);
      budgetApprovedCount  = Number(theProject.budgetApprovedCount);
    }

    let assetArAuthBalance   = Number(this.props.assetArAuthBalance);
    let topUpUnderBalance    = Number(0);

    let topupAmount = null;
    let topupSentence = null;
    if (budgetApprovedCount === 0 && initialBalanceAmount > 0) {
      topupAmount = initialBalanceAmount;
    } else if (assetArAuthBalance < autoTopupThreshold) {
      let candidateAmount = autoTopupAmount - assetArAuthBalance;
      if (candidateAmount > 0) {
          topupAmount = candidateAmount;
      }
      topUpUnderBalance = topupAmount;
    }
            
    //initial balance amount is now dual purpose.  subsequent times it means "requesting one time fee"
    if (budgetApprovedCount > 0 && initialBalanceAmount > 0) {
      if (topupAmount < 0) {
        topupAmount = 0;
        topUpUnderBalance = 0;
      }

      topupAmount = topupAmount + initialBalanceAmount;
    }

    let oneTimeServiceTotal = 0;
    for (let i = 0; i < theAccount.customerServiceTsByCustomerAccountId.nodes.length; i++) {
      let service = theAccount.customerServiceTsByCustomerAccountId.nodes[i];
      //sum up the one-time services that have not already been accepted (exclude cancelled subs)
      if (service.serviceStatus !== 2) {
        if (service.billingFrequency === 0 && service.soAccepted === 0) {
          oneTimeServiceTotal = oneTimeServiceTotal + Number(service.appSkuPrice);
        }
      }
    }

    if (topupAmount) {
      let topupAmountFormatted = Number(topupAmount)
      .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

      if (budgetApprovedCount === 0) {
        topupSentence = "When you authorize this order, you'll be charged the initial funding amount of " + topupAmountFormatted + ".";
      } else {
        let currentBalanceFormatted = Number(assetArAuthBalance)
        .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        let autoTopupAmountFormatted = Number(autoTopupAmount)
        .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        let topUpUnderBalanceFormatted = Number(topUpUnderBalance)
        .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        let initialBalanceAmountFormatted = Number(initialBalanceAmount)
        .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          
        if (budgetApprovedCount > 0 && initialBalanceAmount > 0) {
          if (topUpUnderBalance > 0) {
            topupSentence = "When you authorize this order, you'll be charged " + initialBalanceAmountFormatted + " (plus applicable fees) for the immediate funding request and " + topUpUnderBalanceFormatted + " to bring your balance up to the target amount of " + autoTopupAmountFormatted + ".";

          } else {
            topupSentence = "When you authorize this order, you'll be charged " + initialBalanceAmountFormatted + " (plus applicable fees) for the immediate funding request.";
          }

        } else {
          topupSentence = "Your current balance is " + currentBalanceFormatted + ". When you authorize this order, you'll be charged " + topupAmountFormatted + " to bring your balance up to the target amount of " + autoTopupAmountFormatted + ".";
        }
      }
    }

    if (oneTimeServiceTotal > 0) {
      let oneTimeServiceTotalFormatted = Number(oneTimeServiceTotal)
      .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
      if (topupSentence == null) {
        topupSentence = "When you authorize this order, you'll be billed " + oneTimeServiceTotalFormatted + " (plus applicable fees) for one-time services.";
      } else {
        topupSentence = topupSentence +  "  Also, you'll be billed " + oneTimeServiceTotalFormatted + " for one-time services.";
      }
    }

    if (topupSentence == null && !latestBudgetApproved && autoTopupsActive === true) {
      topupSentence = "Please authorize this order to confirm your subscriptions and wallet settings.";
    }

    if (topupSentence == null) {
      topupSentence = "Please authorize this order to confirm your subscriptions.";
    }

    //let buttonTitle = 'Review subscriptions';
    let buttonTitle = 'Authorize';
    if (autoTopupsActive) {
      if (topupAmount > 0) {
        buttonTitle = 'Authorize and fund budget';
      } else if (! latestBudgetApproved) {
        buttonTitle = 'Authorize';
      }
    }

    /*
    if (autoTopupsActive && (!latestBudgetApproved || topupAmount > 0)) {
      if (topupAmount) {
        buttonTitle = 'Authorize and fund budget';
      } else {
        buttonTitle = 'Authorize';
      }
    }
    */

    if (!displayOnly) {
      return (
        <Grid container spacing={2}>
          {topupSentence &&
          <Grid item xs={12}>
            <Typography>{topupSentence}</Typography>
          </Grid>}

          {this.props.activeStep === this.props.assignedStep &&        
          <Grid item xs={12}>      
            {!this.isBackDisabled() &&
              <Button
                disabled={this.isBackDisabled()}
                color="primary" variant="outlined"
                onClick={() => this.props.handleBack()}
                style={AppBindStyles.bottomButton}>
                Back
              </Button>}               
            {!this.showSpinner() &&
              <Button
                color="primary" variant="contained"
                onClick={() => this.authorizeAndPay()}
                style={AppBindStyles.bottomButton}>
                  {buttonTitle}<ArrowForward/>
              </Button>}
            {this.showSpinner() &&
              <Button color="primary" variant="contained" style={AppBindStyles.bottomButton} >
                <SavingSpinner/>
              </Button>}            
          </Grid>} 
          
          {/* this.props.activeStep === this.props.assignedStep &&        
          <React.Fragment>      
            {!this.isBackDisabled() &&
            <Grid item>
              <Button
                disabled={this.isBackDisabled()}
                color="primary" variant="outlined"
                onClick={() => this.props.handleBack()}
                style={AppBindStyles.bottomButton}>
                Back
              </Button>
            </Grid>}               
            {!this.showSpinner() &&
            <Grid item>
              <Button
                color="primary" variant="contained"
                onClick={() => this.authorizeAndPay()}
                style={AppBindStyles.bottomButton}>
                  {buttonTitle}<ArrowForward/>
              </Button>
            </Grid>}
            {this.showSpinner() &&
            <Grid item>
              <Button color="primary" variant="contained" style={AppBindStyles.bottomButton} >
                <SavingSpinner/>
              </Button>
            </Grid>}            
          </React.Fragment> */} 

        </Grid>       
      );
  
    } else {
      //display only
      return (
        <div></div>
      );
    }
  }     
}
  
CustomerFinalizeStep.propTypes = {
  account: PropTypes.object.isRequired,
  assignedStep: PropTypes.number.isRequired,
  activeStep: PropTypes.number.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default withStyles(styleSheet)(CustomerFinalizeStep);
